import React, { useState } from 'react';
import StaticPage, { Section } from './StaticPage';
import { australianUniversities } from '../../data/universities';

const STATE_LABELS = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  ACT: 'Australian Capital Territory',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
};

const STATE_ORDER = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'ACT', 'TAS', 'NT'];

// Group universities by state
const byState = STATE_ORDER.reduce((acc, state) => {
  acc[state] = australianUniversities.filter(u => u.state === state);
  return acc;
}, {});

// Featured partner universities (highlighted)
const FEATURED = [
  'University of Melbourne',
  'University of New South Wales (UNSW)',
  'University of Queensland',
  'Monash University',
  'RMIT University',
  'Australian National University',
  'University of Western Australia',
  'University of Adelaide',
  'University of Wollongong',
  'University of Technology Sydney (UTS)',
  'Curtin University',
  'University of Sydney',
];

export default function UniversityPartnerships() {
  const [activeState, setActiveState] = useState('ALL');

  const displayed = activeState === 'ALL'
    ? australianUniversities
    : australianUniversities.filter(u => u.state === activeState);

  return (
    <StaticPage
      title="University Partnerships"
      subtitle={`RentalX partners with all ${australianUniversities.length} public universities across Australia to help students find housing faster.`}
    >
      {/* Featured Partners */}
      <Section title="Featured Partner Universities">
        <p className="mb-4">These universities have an active integration or co-marketing agreement with RentalX. Students at these institutions can access RentalX directly from their student portal or accommodation office.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {FEATURED.map((name, i) => {
            const uni = australianUniversities.find(u => u.name === name);
            return (
              <div key={i} className="flex items-start gap-3 p-4 bg-sky-50 border border-[#094f6f]/20 rounded-xl hover:shadow-sm transition-shadow">
                <div className="w-8 h-8 bg-[#094f6f] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {name[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm leading-tight">{name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{uni?.location}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* All Universities */}
      <Section title={`All Australian Universities (${australianUniversities.length})`}>
        {/* State filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['ALL', ...STATE_ORDER].map(s => (
            <button
              key={s}
              onClick={() => setActiveState(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeState === s
                  ? 'bg-[#094f6f] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-sky-100 hover:text-[#094f6f]'
              }`}
            >
              {s === 'ALL' ? `All (${australianUniversities.length})` : `${s} (${byState[s]?.length || 0})`}
            </button>
          ))}
        </div>

        {/* University list */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {displayed.map((uni, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-sky-200 hover:bg-sky-50/50 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 bg-[#094f6f]/10 rounded-full flex items-center justify-center text-[#094f6f] text-xs font-bold flex-shrink-0">
                  {uni.name[0]}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 leading-tight">
                    {uni.name}
                    {FEATURED.includes(uni.name) && (
                      <span className="ml-2 text-[10px] bg-[#094f6f] text-white px-1.5 py-0.5 rounded-full font-semibold">Partner</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">{uni.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* By State breakdown */}
      {activeState === 'ALL' && (
        <Section title="Universities by State">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {STATE_ORDER.filter(s => byState[s]?.length).map(s => (
              <button
                key={s}
                onClick={() => setActiveState(s)}
                className="p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-sky-200 hover:bg-sky-50 transition-all text-left"
              >
                <p className="text-2xl font-black text-[#094f6f]">{byState[s].length}</p>
                <p className="font-semibold text-gray-800 text-sm">{s}</p>
                <p className="text-xs text-gray-500">{STATE_LABELS[s]}</p>
              </button>
            ))}
          </div>
        </Section>
      )}

      <Section title="University of Wollongong — Spotlight">
        <div className="p-6 bg-sky-50 rounded-xl border border-sky-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#094f6f] rounded-full flex items-center justify-center text-white font-black text-lg flex-shrink-0">W</div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">University of Wollongong (UOW)</h3>
              <p className="text-sm text-[#094f6f] font-medium mb-3">Wollongong, NSW · Partner University</p>
              <p className="text-gray-600 text-sm mb-3">UOW is a globally ranked research university located in Wollongong, NSW — just 80km south of Sydney. With over 30,000 students including a strong international cohort, UOW students use RentalX to find accommodation in the Wollongong CBD, Fairy Meadow, and surrounding suburbs.</p>
              <div className="flex gap-4 text-sm">
                <span className="text-gray-600">🎓 30,000+ students</span>
                <span className="text-gray-600">🌍 Top 1% globally</span>
                <span className="text-gray-600">🏠 Listings from A$220/wk</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Become a Partner University">
        <p>We'd love to work with your institution. Email <strong>universities@rentalx.com.au</strong> to discuss how we can help your students find verified housing before they arrive.</p>
      </Section>
    </StaticPage>
  );
}
